// src/pages/shop/ShopPurchase.jsx
//
// ПРИХОД (поставка от поставщика) — зеркало оптовой отгрузки.
// Там отдаём товар оптовику, тут принимаем от поставщика.
//
// Ключевое: в остатки идёт то, что ФАКТИЧЕСКИ принято (что набил здесь),
// а не то, что написано в бумажке поставщика. Бумажку сверяешь глазами.

import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  counterparties as cpApi,
  products as productsApi,
  categories as categoriesApi,
  purchases as purchasesApi,
} from '../../api';
import { useToast } from '../../hooks/useToast';
import { useMyCurrency, useMyShop } from '../../hooks/useMyShop';
import { isModuleOn } from '../../config/modules';
import { formatMoney } from '../../data/currencies';
import { errorMessage } from '../../utils/errorMessage';
import Icon from '../../components/Icon';
import SafeInput from '../../components/SafeInput';
import VariantPickerSheet from '../../components/VariantPickerSheet';
import ScanButton from '../../components/ScanButton';

export default function ShopPurchase() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { show, ToastView } = useToast();
  const currency = useMyCurrency();
  const myShop = useMyShop();
  const money = (v) => formatMoney(v, currency);

  const [suppliers, setSuppliers] = useState([]);
  const [supplierId, setSupplierId] = useState('');
  const [allProducts, setAllProducts] = useState([]);
  const [allCategories, setAllCategories] = useState([]);
  const [selectedCatId, setSelectedCatId] = useState('all');
  const [search, setSearch] = useState('');
  const [cart, setCart] = useState([]);
  const [note, setNote] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [variantPickerProduct, setVariantPickerProduct] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const [cps, ps, cats] = await Promise.all([
          cpApi.list({ type: 'supplier' }),          // бэк сам отфильтрует поставщиков
          productsApi.list().catch(() => []),
          categoriesApi.list().catch(() => []),
        ]);
        // ВАЖНО: list() отдаёт { items: [...] }, а не голый массив
        setSuppliers(cps.items || []);
        setAllProducts(Array.isArray(ps) ? ps : (ps.items || []));
        setAllCategories(Array.isArray(cats) ? cats : (cats.items || []));
      } catch (e) {
        show(errorMessage(e, t), 'error');
      }
      setLoading(false);
    })();
  }, []); // eslint-disable-line

  // ─── Корзина прихода ──────────────────────────────────────────
  const addItem = (product, variant) => {
    const key = variant ? `v:${variant.id}` : `p:${product.id}`;
    setCart(prev => {
      const found = prev.find(i => i.key === key);
      if (found) {
        return prev.map(i => i.key === key ? { ...i, qty: i.qty + 1 } : i);
      }
      return [...prev, {
        key,
        productId: product.id,
        variantId: variant?.id || null,
        name: product.name,
        variantName: variant?.name || null,
        unit: product.unit || t('common.pieces'),
        qty: 1,
        // Закупочную цену подставляем из карточки — но её всегда можно поправить:
        // поставщик мог поднять цену, в этом весь смысл прихода.
        costPrice: Number(variant?.costPrice ?? product.costPrice ?? 0),
        shelfLifeDays: '',
      }];
    });
  };

  const addProduct = (product) => {
    const variants = product.variants || [];
    if (variants.length > 1) {
      setVariantPickerProduct(product);
      return;
    }
    addItem(product, variants[0] || null);
  };

  // Сканирование штрихкода — найти товар и добавить в приход.
  // При приёмке это основной способ:сканируешь коробку за коробкой.
  const handleScan = async (code) => {
    try {
      const r = await productsApi.byBarcode(code);
      if (r?.product) {
        if (r.variant) { addItem(r.product, r.variant); return; }
        addProduct(r.product);
        return;
      }
      show(t('products.barcodeNotFound'), 'error');
    } catch (e) {
      show(errorMessage(e, t), 'error');
    }
  };

  const setQty = (key, qty) =>
    setCart(prev => prev.map(i => i.key === key ? { ...i, qty: Math.max(0, Number(qty) || 0) } : i));

  const setCost = (key, costPrice) =>
    setCart(prev => prev.map(i => i.key === key ? { ...i, costPrice: Math.max(0, Number(costPrice) || 0) } : i));

  const setShelfLife = (key, days) =>
    setCart(prev => prev.map(i => i.key === key ? { ...i, shelfLifeDays: days } : i));

  const removeItem = (key) => setCart(prev => prev.filter(i => i.key !== key));

  const total = useMemo(
    () => cart.reduce((s, i) => s + (Number(i.qty) || 0) * (Number(i.costPrice) || 0), 0),
    [cart],
  );

  // ─── Фильтр товаров ───────────────────────────────────────────
  const visibleProducts = useMemo(() => {
    let list = allProducts;
    if (selectedCatId !== 'all') {
      list = list.filter(p => p.categoryId === selectedCatId);
    }
    const q = search.trim().toLowerCase();
    if (q) {
      list = list.filter(p =>
        (p.name || '').toLowerCase().includes(q) ||
        (p.article || '').toLowerCase().includes(q) ||
        (p.barcode || '').toLowerCase().includes(q),
      );
    }
    return list;
  }, [allProducts, selectedCatId, search]);

  // ─── Сохранение ───────────────────────────────────────────────
  const save = async () => {
    if (!supplierId) return show(t('purchase.errNoSupplier'), 'error');
    const items = cart.filter(i => i.qty > 0);
    if (items.length === 0) return show(t('purchase.errEmpty'), 'error');

    setSaving(true);
    try {
      const created = await purchasesApi.create({
        counterpartyId: supplierId,
        note: note.trim() || undefined,
        items: items.map(i => ({
          productId: i.productId,
          variantId: i.variantId,
          qty: i.qty,
          costPrice: i.costPrice,
          shelfLifeDays: Number(i.shelfLifeDays) > 0 ? Number(i.shelfLifeDays) : null,
        })),
      });
      show(t('purchase.created', { number: created.number }));
      navigate(`/purchase/${created.id}`, { replace: true });
    } catch (e) {
      show(errorMessage(e, t), 'error');
    }
    setSaving(false);
  };

  if (loading) {
    return <div className="page"><p style={{ padding: 24, color: 'var(--fg-muted)' }}>{t('common.loading')}</p></div>;
  }

  return (
    <div className="page" style={{ paddingBottom: cart.length ? 140 : 24 }}>
      <div className="page-header">
        <button
          onClick={() => navigate(-1)}
          style={{ background: 'none', border: 'none', padding: 8, marginLeft: -8, cursor: 'pointer', color: 'var(--fg-muted)' }}
          aria-label={t('common.back')}
        >
          <Icon name="chevronLeft" size={22} stroke={2.5} />
        </button>
        <div style={{ flex: 1 }}>
          <p className="page-subtitle">{t('purchase.subtitle')}</p>
          <h1 className="page-title">{t('purchase.title')}</h1>
        </div>
        <button
          onClick={() => navigate('/purchases')}
          style={{
            width: 40, height: 40, borderRadius: 'var(--r-sm)',
            background: 'var(--bg-elevated)', border: '1px solid var(--border)',
            color: 'var(--fg-muted)', display: 'flex', alignItems: 'center', justifyContent: 'center',
            cursor: 'pointer',
          }}
          aria-label={t('purchase.history')}
        >
          <Icon name="list" size={18} stroke={2} />
        </button>
      </div>

      {/* Поставщик */}
      <div className="input-group">
        <label>{t('purchase.supplier')}</label>
        {suppliers.length === 0 ? (
          <div className="card" style={{ padding: 14 }}>
            <p style={{ fontSize: 13, color: 'var(--fg-muted)', marginBottom: 10, lineHeight: 1.5 }}>
              {t('purchase.noSuppliers')}
            </p>
            <button className="btn btn-secondary" onClick={() => navigate('/counterparties')}>
              {t('wholesale.goToCounterparties')}
            </button>
          </div>
        ) : (
          <select className="select" value={supplierId} onChange={e => setSupplierId(e.target.value)}>
            <option value="">{t('purchase.pickSupplier')}</option>
            {suppliers.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
        )}
      </div>

      {/* Позиции прихода */}
      {cart.length > 0 && (
        <div className="card" style={{ marginBottom: 12 }}>
          <p style={{ fontSize: 11, fontWeight: 700, color: 'var(--fg-muted)', letterSpacing: '0.06em', marginBottom: 10 }}>
            {t('purchase.itemsHeader')} · {cart.length}
          </p>
          {cart.map(i => (
            <div key={i.key} style={{ paddingBottom: 12, marginBottom: 12, borderBottom: '1px solid var(--border)' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                <span style={{ flex: 1, fontSize: 14, fontWeight: 600 }}>
                  {i.name}
                  {i.variantName && <span style={{ color: 'var(--fg-muted)', fontWeight: 500 }}> · {i.variantName}</span>}
                </span>
                <button
                  onClick={() => removeItem(i.key)}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--danger)', padding: 4 }}
                  aria-label={t('common.remove')}
                >
                  <Icon name="x" size={16} stroke={2.5} />
                </button>
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <div style={{ flex: 1 }}>
                  <label style={{ fontSize: 10, color: 'var(--fg-muted)', display: 'block', marginBottom: 3 }}>
                    {t('purchase.qtyFact')}
                  </label>
                  <SafeInput
                    className="input"
                    type="number"
                    inputMode="decimal"
                    value={i.qty}
                    onChange={e => setQty(i.key, e.target.value)}
                    style={{ padding: '8px 10px', fontSize: 14 }}
                  />
                </div>
                <div style={{ flex: 1 }}>
                  <label style={{ fontSize: 10, color: 'var(--fg-muted)', display: 'block', marginBottom: 3 }}>
                    {t('purchase.costPrice')}
                  </label>
                  <SafeInput
                    className="input"
                    type="number"
                    inputMode="decimal"
                    value={i.costPrice}
                    onChange={e => setCost(i.key, e.target.value)}
                    style={{ padding: '8px 10px', fontSize: 14 }}
                  />
                </div>
                <div style={{ width: 90 }}>
                  <label style={{ fontSize: 10, color: 'var(--fg-muted)', display: 'block', marginBottom: 3 }}>
                    {t('purchase.sum')}
                  </label>
                  <div style={{
                    padding: '8px 10px', fontSize: 14, fontWeight: 700,
                    background: 'var(--bg-subtle)', borderRadius: 'var(--r-sm)',
                    textAlign: 'right', whiteSpace: 'nowrap', overflow: 'hidden',
                  }}>
                    {money((Number(i.qty) || 0) * (Number(i.costPrice) || 0))}
                  </div>
                </div>
              </div>
              {isModuleOn(myShop, 'perishable') && (
                <div style={{ marginTop: 8 }}>
                  <label style={{ fontSize: 10, color: 'var(--fg-muted)', display: 'block', marginBottom: 3 }}>
                    {t('perishable.shelfLifeField', 'Срок годности (дней) — если скоропорт')}
                  </label>
                  <SafeInput
                    className="input"
                    type="number"
                    inputMode="numeric"
                    value={i.shelfLifeDays}
                    onChange={e => setShelfLife(i.key, e.target.value)}
                    placeholder={t('perishable.shelfLifePh', 'напр. 7 — пусто, если не портится')}
                    style={{ padding: '8px 10px', fontSize: 14 }}
                  />
                </div>
              )}
            </div>
          ))}
          <div className="input-group" style={{ marginBottom: 0 }}>
            <label>{t('purchase.note')}</label>
            <SafeInput
              className="input"
              value={note}
              onChange={e => setNote(e.target.value)}
              placeholder={t('purchase.notePh')}
            />
          </div>
        </div>
      )}

      {/* Выбор товаров */}
      <div className="input-group">
        <label>{t('purchase.addProducts')}</label>
        <div style={{ display: 'flex', gap: 8 }}>
          <SafeInput
            className="input"
            style={{ flex: 1 }}
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder={t('wholesale.searchProduct')}
            autoCapitalize="off"
            spellCheck="false"
          />
          <ScanButton onScan={handleScan} size="lg" />
        </div>
      </div>

      {allCategories.length > 0 && (
        <div style={{ display: 'flex', gap: 6, overflowX: 'auto', marginBottom: 12, paddingBottom: 4 }}>
          <Chip active={selectedCatId === 'all'} onClick={() => setSelectedCatId('all')}>
            {t('wholesale.allCategories')}
          </Chip>
          {allCategories.map(c => (
            <Chip key={c.id} active={selectedCatId === c.id} onClick={() => setSelectedCatId(c.id)}>
              {c.name}
            </Chip>
          ))}
        </div>
      )}

      {visibleProducts.length === 0 ? (
        <p style={{ fontSize: 13, color: 'var(--fg-muted)', textAlign: 'center', padding: '24px 0' }}>
          {t('wholesale.noProductsFound')}
        </p>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {visibleProducts.map(p => (
            <button
              key={p.id}
              onClick={() => addProduct(p)}
              className="card"
              style={{
                display: 'flex', alignItems: 'center', gap: 10,
                padding: 12, textAlign: 'left', cursor: 'pointer', border: 'none',
              }}
            >
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 14, fontWeight: 600 }}>{p.name}</div>
                <div style={{ fontSize: 12, color: 'var(--fg-muted)' }}>
                  {t('products.stockShort')}: {p.stock ?? 0} {p.unit}
                  {(p.variants?.length > 1) && ` · ${t('wholesale.variantsCount')}: ${p.variants.length}`}
                </div>
              </div>
              <Icon name="plus" size={18} stroke={2.5} color="var(--brand-primary)" />
            </button>
          ))}
        </div>
      )}

      {/* Итог и сохранение */}
      {cart.length > 0 && (
        <div style={{
          position: 'fixed', left: 0, right: 0, bottom: 0,
          padding: 16, paddingBottom: 'calc(16px + env(safe-area-inset-bottom))',
          background: 'var(--bg)', borderTop: '1px solid var(--border)',
          zIndex: 50,
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 10 }}>
            <span style={{ fontSize: 13, color: 'var(--fg-muted)' }}>{t('purchase.total')}</span>
            <span style={{ fontSize: 18, fontWeight: 800, fontFamily: 'var(--font-display)' }}>{money(total)}</span>
          </div>
          <button className="btn btn-primary btn-lg" onClick={save} disabled={saving} style={{ width: '100%' }}>
            {saving ? t('common.saving') : t('purchase.receiveBtn', { count: cart.length })}
          </button>
        </div>
      )}

      {ToastView}

      {variantPickerProduct && (
        <VariantPickerSheet
          product={variantPickerProduct}
          currency={currency}
          onPick={(variant) => { addItem(variantPickerProduct, variant); setVariantPickerProduct(null); }}
          onClose={() => setVariantPickerProduct(null)}
        />
      )}
    </div>
  );
}

function Chip({ active, onClick, children }) {
  return (
    <button
      onClick={onClick}
      style={{
        flexShrink: 0,
        padding: '7px 13px',
        borderRadius: 999,
        border: '1px solid ' + (active ? 'var(--brand-primary)' : 'var(--border)'),
        background: active ? 'var(--brand-primary)' : 'var(--bg-elevated)',
        color: active ? '#fff' : 'var(--fg-muted)',
        fontSize: 13, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap',
      }}
    >
      {children}
    </button>
  );
}
